import React, { useCallback, useEffect, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { ArrowLeft, Check, RotateCcw, RotateCw, ZoomIn, ZoomOut } from 'lucide-react';
import { getCroppedBlob } from '../utils/cropImage';
import { t } from '../i18n';

const ACCENT = '#3fced1';

/** Feste Formate (Marcels Regel: jedes Bild-Feld hat ein festes Seitenverhältnis). */
export const CROP_ASPECTS = { square: 1, wide: 16 / 9, portrait: 4 / 5 } as const;
export type CropAspectKey = keyof typeof CROP_ASPECTS;
/**
 * Freies Format (Ausnahme für Beiträge, Marcel 2026-10-20: „man sollte in allen Formaten posten können“):
 * der Nutzer wählt im Editor zwischen Original, 1:1, 4:5 und 16:9. Der Feed zeigt jedes Format vollständig
 * (BlurredImg), deshalb ist hier kein festes Seitenverhältnis nötig.
 */
export type CropAspectInput = CropAspectKey | number | 'free';
export type FreeAspectKey = 'original' | CropAspectKey;
export const FREE_ASPECT_KEYS: FreeAspectKey[] = ['original', 'square', 'portrait', 'wide'];

/**
 * PURE: Seitenverhältnis für react-easy-crop. „original“ = natürliches Format des Bildes, bei 90°/270°
 * gedreht entsprechend gekippt; solange das Bild nicht geladen ist, 1:1.
 */
export const resolveCropRatio = ({ aspect, freeKey, naturalRatio, baseRotation }: { aspect: CropAspectInput; freeKey: FreeAspectKey; naturalRatio: number | null; baseRotation: number }): number => {
  if (typeof aspect === 'number') return aspect;
  if (aspect !== 'free') return CROP_ASPECTS[aspect];
  if (freeKey !== 'original') return CROP_ASPECTS[freeKey];
  if (!naturalRatio || !Number.isFinite(naturalRatio) || naturalRatio <= 0) return 1;
  const quarter = ((baseRotation % 360) + 360) % 360;
  return quarter === 90 || quarter === 270 ? 1 / naturalRatio : naturalRatio;
};

interface Props {
  open: boolean;
  imageSrc: string | null;
  /** Format-Schlüssel, Zahl (Breite/Höhe) oder 'free' (Formatwahl im Editor, Beiträge) */
  aspect?: CropAspectInput;
  /** Runde Vorschau (Avatar); das Ergebnis ist trotzdem ein Quadrat */
  round?: boolean;
  title?: string;
  maxSize?: number;
  onCancel: () => void;
  onDone: (blob: Blob) => void | Promise<void>;
}

/**
 * Wiederverwendbarer Vollbild-Bildeditor (2026-09-17): Zuschneiden, Zoom, Drehen,
 * festes Seitenverhältnis. Liegt als fixed inset-0 ÜBER der Navbar mit
 * App-Style-Kopfzeile (Zurück-Pfeil + Titel). Erster Einsatz: Profilbild (1:1).
 */
const ImageCropperModal: React.FC<Props> = ({ open, imageSrc, aspect = 'square', round = false, title, maxSize = 1600, onCancel, onDone }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [baseRotation, setBaseRotation] = useState(0);
  const [fineRotation, setFineRotation] = useState(0);
  const rotation = (baseRotation + fineRotation + 360) % 360;
  const [area, setArea] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);
  const [freeKey, setFreeKey] = useState<FreeAspectKey>('original');
  const [naturalRatio, setNaturalRatio] = useState<number | null>(null);
  const ratio = resolveCropRatio({ aspect, freeKey, naturalRatio, baseRotation });

  useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setBaseRotation(0);
      setFineRotation(0);
      setArea(null);
      setBusy(false);
      setFreeKey('original');
      setNaturalRatio(null);
    }
  }, [open, imageSrc]);

  // Freies Format: natürliches Seitenverhältnis des Bildes laden (für „Original“)
  useEffect(() => {
    if (!open || aspect !== 'free' || !imageSrc) return;
    let cancelled = false;
    const img = new Image();
    img.onload = () => { if (!cancelled && img.naturalWidth > 0 && img.naturalHeight > 0) setNaturalRatio(img.naturalWidth / img.naturalHeight); };
    img.src = imageSrc;
    return () => { cancelled = true; };
  }, [open, aspect, imageSrc]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onCancel]);

  const onCropComplete = useCallback((_: Area, pixels: Area) => setArea(pixels), []);

  const apply = async () => {
    if (!imageSrc || !area || busy) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(imageSrc, area, rotation, { maxSize });
      await onDone(blob);
    } finally {
      setBusy(false);
    }
  };

  if (!open || !imageSrc) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex flex-col bg-zinc-950 text-white" role="dialog" aria-modal="true" aria-label={title ?? t('web.component.image-cropper.bild-bearbeiten')}>
      {/* Kopfzeile im App-Stil */}
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-zinc-800 px-3">
        <button type="button" onClick={onCancel} className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-300 hover:bg-zinc-900 hover:text-white" aria-label={t('web.component.image-cropper.abbrechen')}>
          <ArrowLeft size={20} />
        </button>
        <h2 className="flex-1 truncate text-base font-bold">{title ?? t('web.component.image-cropper.bild-bearbeiten')}</h2>
        <button
          type="button"
          onClick={apply}
          disabled={busy || !area}
          className="flex h-10 items-center gap-1.5 rounded-xl px-4 text-sm font-semibold text-black disabled:opacity-60"
          style={{ backgroundColor: ACCENT }}
        >
          <Check size={16} />
          {busy ? t('web.component.image-cropper.wird-uebernommen') : t('web.component.image-cropper.uebernehmen')}
        </button>
      </div>

      {/* Bildfläche */}
      <div className="relative min-h-0 flex-1 bg-black">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={ratio}
          cropShape={round ? 'round' : 'rect'}
          showGrid={!round}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
          minZoom={1}
          maxZoom={4}
          zoomWithScroll
          restrictPosition
          style={{ cropAreaStyle: { border: `2px solid ${ACCENT}`, boxShadow: '0 0 0 9999px rgba(0,0,0,0.7)' } }}
        />
      </div>

      {/* Werkzeuge */}
      <div className="shrink-0 border-t border-zinc-800 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="mx-auto flex max-w-xl flex-col gap-3">
          {aspect === 'free' && (
            <div className="flex flex-wrap items-center gap-2" data-testid="crop-aspect-chips">
              <span className="text-xs text-gray-400">{t('web.component.image-cropper.format')}</span>
              {FREE_ASPECT_KEYS.map((k) => (
                <button key={k} type="button" onClick={() => setFreeKey(k)} aria-pressed={freeKey === k} data-testid={`crop-aspect-${k}`}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${freeKey === k ? 'border-[#3fced1] bg-[#3fced1] text-black' : 'border-zinc-700 text-zinc-300 hover:border-zinc-500'}`}>
                  {t(`web.component.image-cropper.format-${k}`)}
                </button>
              ))}
            </div>
          )}
          <label className="flex items-center gap-3 text-xs text-gray-400">
            <ZoomOut size={16} className="shrink-0" />
            <input type="range" min={1} max={4} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-[#3fced1]" aria-label={t('web.component.image-cropper.zoom')} />
            <ZoomIn size={16} className="shrink-0" />
          </label>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setBaseRotation((r) => (r - 90 + 360) % 360)} className="flex h-10 w-10 items-center justify-center rounded-xl border border-zinc-700 text-gray-200 hover:border-[#3fced1]/60" aria-label={t('web.component.image-cropper.nach-links-drehen')} title={t('web.component.image-cropper.nach-links-drehen')}>
              <RotateCcw size={18} />
            </button>
            <input type="range" min={-45} max={45} step={1} value={fineRotation} onChange={(e) => setFineRotation(Number(e.target.value))} className="flex-1 accent-[#3fced1]" aria-label={t('web.component.image-cropper.feinjustierung')} />
            <button type="button" onClick={() => setBaseRotation((r) => (r + 90) % 360)} className="flex h-10 w-10 items-center justify-center rounded-xl border border-zinc-700 text-gray-200 hover:border-[#3fced1]/60" aria-label={t('web.component.image-cropper.nach-rechts-drehen')} title={t('web.component.image-cropper.nach-rechts-drehen')}>
              <RotateCw size={18} />
            </button>
          </div>
          <p className="text-center text-[11px] text-gray-500">{t('web.component.image-cropper.hinweis')}</p>
        </div>
      </div>
    </div>
  );
};

export default ImageCropperModal;
